import { Injectable } from "@angular/core";
import { AppConfig } from "src/app/app.config";
import { WithdrawalsService } from "src/app/services/http/withdrawals.service";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { SnackBarService } from "src/app/services/snackbar/snackBar.service";
import { CashflowFacade } from "../../cashflow/facade/cashflow.facade";
import {
    ICreateWithdrawalPayload,
    IRegularWithdrawalResponse,
    IWithdrawalInstructionResponse,
} from "../../../models/withdrawals";
import { GeneralSelectionFacade } from "../../general-selection/facade/general-selection.facade";
import { PortfoliosFacade } from "../../portfolios/facade/portfolios.facade";
import { sharedDeduplicatingSwitchMap } from "src/app/utils/deduplicatingSwitchMap";

@Injectable({
    providedIn: "root", // Ensures singleton instance across the app
})
export class WithdrawalFacade {
    /**
     * Just a subject that we can fire events to trigger loading of withdrawals and regular withdrawals. Only used internally.
     */
    private loadWithdrawals = new BehaviorSubject(null);

    /**
     * Observable that will fire with the most recent withdrawals based off the currently selected entities/portfolios
     */
    public withdrawals$ = combineLatest([
        this.generalSelectionFacade.getSelectedEntityPortfolioState$,
        this.loadWithdrawals.asObservable(),
    ]).pipe(
        map(([generalState, _]) => {
            return this.getPortfolioIds(generalState.selectedPortfolios, []);
        }),
        sharedDeduplicatingSwitchMap((portfolioIds) => {
            if (portfolioIds.length === 0) {
                return of({
                    loading: false,
                    data: [],
                    error: false,
                } as IWithdrawalInstructionResponse);
            }
            
            return this.loadWithdrawalInstructions(portfolioIds);
        }),
    );

    /**
     * Observable that will fire with the most recent regular withdrawals based off the currently selected entities/portfolios
     */
    public regularWithdrawals$ = combineLatest([
        this.generalSelectionFacade.getSelectedEntityPortfolioState$,
        this.loadWithdrawals.asObservable(),
    ]).pipe(
        map(([generalState, _]) => this.getPortfolioIds(generalState.selectedPortfolios, [])),
        sharedDeduplicatingSwitchMap((portfolioIds) => {
            if (portfolioIds.length === 0) {
                return of({
                    loading: false,
                    data: [],
                    error: false,
                } as IRegularWithdrawalResponse);
            }
            return this.loadRegularWithdrawalInstructions(portfolioIds);
        }),
    );

    constructor(
        private appConfig: AppConfig,
        private withdrawalService: WithdrawalsService,
        private snackBarService: SnackBarService,
        private cashflowFacade: CashflowFacade,
        private generalSelectionFacade: GeneralSelectionFacade,
        private portfolioFacade: PortfoliosFacade,
    ) {}

    public load() {
        this.loadWithdrawals.next(null);
    }

    public isWithdrawalsEnabled(isAdministrator: boolean): boolean {
        let isWithdrawalsEnabledForManager = this.appConfig.isWithdrawalsEnabledForManagerUsers();

        if (isWithdrawalsEnabledForManager && isAdministrator) return true;

        return this.appConfig.isWithdrawalsEnabledForInvestorUsers();
    }

    /**
     * Dispatches a request to load withdrawal instructions for the given portfolios.
     */
    private loadWithdrawalInstructions(portfolioIds: number[]): Observable<IWithdrawalInstructionResponse> {
        return this.withdrawalService.getWithdrawalInstructions(portfolioIds);
    }

    private loadRegularWithdrawalInstructions(portfolioIds: number[]): Observable<IRegularWithdrawalResponse> {
        return this.withdrawalService.getRegularWithdrawalPlans(portfolioIds);
    }

    /**
     * Attempts to approve a withdrawal instruction.
     */
    public approveWithdrawalInstruction(portfolioId: number, instructionId: number): Observable<boolean> {
        return this.withdrawalService.approveWithdrawalInstruction(portfolioId, instructionId).pipe(
            map(() => {
                this.snackBarService.showSnackBar("Successfully approved withdrawal instruction");
                return true;
            }),
            catchError(() => {
                this.snackBarService.showWarningSnackBar("Failed to approve withdrawal instruction");
                return of(false);
            }),
        );
    }

    /**
     * Attempts to approve a regular withdrawal instruction.
     */
    public approveRegularWithdrawal(portfolioId: number, regularWithdrawalId: number) {
        return this.withdrawalService.approveRegularWithdrawal(portfolioId, regularWithdrawalId).pipe(
            map(() => {
                this.snackBarService.showSnackBar("Successfully approved regular withdrawal");
                return true;
            }),
            catchError(() => {
                this.snackBarService.showWarningSnackBar("Failed to approve regular withdrawal");
                return of(false);
            }),
        );
    }

    private generateWithdrawalSuccessMessage(res: any): string {
        return `Success! Your withdrawal of <b>${res.amount.value.toFixed(2)} ${res.amount.currency}</b> 
        has been created. Please allow one working day for it to appear in the nominated bank account.`;
    }

    /**
     * After a withdrawal we refresh the cashflow for todays date (not the date set in the general selection state).
     * This is because a withdrawal affects our current cashflow balance, not a cashflow balance in the past.
     */
    private refreshAvailableCashForTodaysDate(): void {
        this.cashflowFacade.requestAvailableCashflowData(true);
    }

    public createCashWithdrawal(payload: ICreateWithdrawalPayload): Observable<Object> {
        return this.withdrawalService.createNewCashWithdrawal(payload).pipe(
            tap((res) => {
                this.refreshAvailableCashForTodaysDate();
                this.snackBarService.showSnackBar(this.generateWithdrawalSuccessMessage(res));
                this.load();
            }),
            catchError(() => {
                this.snackBarService.showWarningSnackBar("Failed to create withdrawal");
                return of();
            }),
        );
    }

    /**
     * Retrieves portfolio ids based on either selectedPortfolios or the portfolios of selectedEntities from the state.
     */
    private getPortfolioIds(selectedPortfolios, portfoliosOfSelectedEntities): number[] {
        return selectedPortfolios.length > 0
            ? selectedPortfolios.map((x) => x.id)
            : portfoliosOfSelectedEntities.map((x) => x.id);
    }
}
